/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-enum-comparison */
import isEqual from 'lodash.isequal';

import type { ContentStatusFilterType } from 'components/search-content/components/search-content-filters/components/status-filter/StatusFilter';
import {
  ArticleItem,
  MobileArticleItem,
  MobileLinkedItem,
  PublicationSetting,
  PublicationSettingSearchableContentType,
  QueryConfig
} from 'data/generated/graphql';
import { AllessehQueryRuleTerm, AllessehQueryRuleTerms } from 'hooks/useAllessehContentQuery';
import { MOBILE_LINKED_ITEM } from './constants';
import { safelyParseContent } from './temp';

export interface ContentLabel {
  name: string;
  category: 'type' | 'brand';
}

export const getContentLabel = (type: string): ContentLabel => {
  switch (type.toLowerCase()) {
    case PublicationSettingSearchableContentType.CommerceProduct.toLowerCase():
      return { name: 'Product', category: 'type' };
    case PublicationSettingSearchableContentType.LiveCoverage.toLowerCase():
      return { name: 'Live Coverage', category: 'type' };
    case PublicationSettingSearchableContentType.LiveCoverageCard.toLowerCase():
      return { name: 'Live Coverage card', category: 'type' };
    case PublicationSettingSearchableContentType.LiveCoverageStack.toLowerCase():
      return { name: 'Live Coverage event', category: 'type' };
    case PublicationSettingSearchableContentType.DowJonesRiskJournalCcoEvents.toLowerCase():
      return { name: 'Dow Jones Risk Journal CCO Events', category: 'brand' };
    case PublicationSettingSearchableContentType.DowJonesRiskJournalContributor.toLowerCase():
      return { name: 'Dow Jones Risk Journal Contributor', category: 'brand' };
    case PublicationSettingSearchableContentType.DowJonesRiskJournalDjEvents.toLowerCase():
      return { name: 'Dow Jones Risk Journal DJ Events', category: 'brand' };
    case PublicationSettingSearchableContentType.WorldEcr.toLowerCase():
      return { name: 'World ECR', category: 'brand' };
    case PublicationSettingSearchableContentType.ExportComplianceManager.toLowerCase():
      return { name: 'Export Compliance Manager', category: 'brand' };
    case PublicationSettingSearchableContentType.OxfordAnalytica.toLowerCase():
      return { name: 'Oxford Analytica', category: 'brand' };
    case PublicationSettingSearchableContentType.Dragonfly.toLowerCase():
      return { name: 'Dragonfly', category: 'brand' };
    case PublicationSettingSearchableContentType.DowJonesRiskJournal.toLowerCase():
      return { name: 'Dow Jones Risk Journal', category: 'brand' };
    case PublicationSettingSearchableContentType.Marketwatch.toLowerCase():
      return { name: 'MarketWatch News', category: 'brand' };
    case PublicationSettingSearchableContentType.MarketwatchLiveCoverageCards.toLowerCase():
      return { name: 'MarketWatch Live Coverage Cards', category: 'brand' };
    case PublicationSettingSearchableContentType.MarketwatchLiveCoverageEvents.toLowerCase():
      return { name: 'MarketWatch Live Coverage Events', category: 'brand' };
    case PublicationSettingSearchableContentType.MarketwatchVideo.toLowerCase():
      return { name: 'MarketWatch Video', category: 'brand' };
    case PublicationSettingSearchableContentType.Wsj.toLowerCase():
      return { name: 'WSJ News', category: 'brand' };
    case PublicationSettingSearchableContentType.WsjVideo.toLowerCase():
      return { name: 'WSJ Video', category: 'brand' };
    case PublicationSettingSearchableContentType.WsjLiveCoverageCards.toLowerCase():
      return { name: 'WSJ Live Coverage Cards', category: 'brand' };
    case PublicationSettingSearchableContentType.WsjLiveCoverageEvents.toLowerCase():
      return { name: 'WSJ Live Coverage Events', category: 'brand' };
    case PublicationSettingSearchableContentType.WsjOpinionVideo.toLowerCase():
      return { name: 'WSJ Opinion Video', category: 'brand' };
    case PublicationSettingSearchableContentType.Barrons.toLowerCase():
      return { name: "Barron's", category: 'brand' };
    case PublicationSettingSearchableContentType.BarronsLiveCoverageCards.toLowerCase():
      return { name: "Barron's Live Coverage Cards", category: 'brand' };
    case PublicationSettingSearchableContentType.BarronsLiveCoverageEvents.toLowerCase():
      return { name: "Barron's Live Coverage Events", category: 'brand' };
    case PublicationSettingSearchableContentType.BarronsVideo.toLowerCase():
      return { name: "Barron's Video", category: 'brand' };
    case PublicationSettingSearchableContentType.FnOnline.toLowerCase():
      return { name: 'Financial News', category: 'brand' };
    case PublicationSettingSearchableContentType.Penews.toLowerCase():
      return { name: 'PEN', category: 'brand' };
    case PublicationSettingSearchableContentType.WsjPro.toLowerCase():
    case PublicationSettingSearchableContentType.WsjProPe.toLowerCase():
      return { name: 'WSJ Pro', category: 'brand' };
    case PublicationSettingSearchableContentType.OxanAnnouncements.toLowerCase():
      return { name: 'Announcements', category: 'type' };
    case PublicationSettingSearchableContentType.FreeExpression.toLowerCase():
      return { name: 'Free Expression', category: 'brand' };
    case PublicationSettingSearchableContentType.Ibd.toLowerCase():
      return { name: 'Investor Business Daily', category: 'brand' };
    case PublicationSettingSearchableContentType.BarronsBlog.toLowerCase():
      return { name: "Barron's Blog", category: 'brand' };
    case PublicationSettingSearchableContentType.PrivateMarketEvents.toLowerCase():
      return { name: 'Private Market Events', category: 'type' };
    case PublicationSettingSearchableContentType.Opinion.toLowerCase():
      return { name: 'Opinion', category: 'brand' };
    case PublicationSettingSearchableContentType.Penta.toLowerCase():
      return { name: 'Penta', category: 'brand' };
    case PublicationSettingSearchableContentType.MansionGlobal.toLowerCase():
      return { name: 'Mansion Global', category: 'brand' };
    case PublicationSettingSearchableContentType.Afp.toLowerCase():
      return { name: 'AFP', category: 'brand' };
    case PublicationSettingSearchableContentType.BarronsInvestorCircle.toLowerCase():
      return { name: "Barron's Investor Circle", category: 'brand' };
    case PublicationSettingSearchableContentType.EnergyNewsEvents.toLowerCase():
      return { name: 'Energy News Events', category: 'type' };
    default:
      return { name: type, category: 'type' };
  }
};

export const getStatusLabel = (status: ContentStatusFilterType): string => {
  const LABELS: Record<ContentStatusFilterType, string> = {
    PUBLISHED: 'Published',
    EMAIL_ONLY: 'Email Only',
    EMBARGO: 'Embargo',
    SCHEDULED_PUBLISH: 'Scheduled Publish'
  };

  return LABELS[status];
};

const addToMergedQuery = (clauses: AllessehQueryRuleTerms[], key: string, value: string | string[]) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const existingClause = clauses.find((clause) => clause.terms && clause.terms.key === key);
  if (existingClause) {
    if (Array.isArray(value)) {
      value.forEach((val) => {
        if (existingClause.terms.value.includes(val)) return;

        existingClause.terms.value.push(val);
      });
    } else {
      if (existingClause.terms.value.includes(value)) {
        return;
      }
      existingClause.terms.value.push(value);
    }
  } else if (Array.isArray(value)) {
    clauses.push({
      terms: {
        key,
        value
      }
    });
  } else {
    clauses.push({
      terms: {
        key,
        value: [value]
      }
    });
  }
};

// normalize a rule by sorting its value array
const normalizeRule = (rule: AllessehQueryRuleTerms): AllessehQueryRuleTerms => ({
  terms: {
    key: rule.terms.key,
    value: [...rule.terms.value].sort()
  }
});

function dedupeRules(rules: AllessehQueryRuleTerms[]): AllessehQueryRuleTerms[] {
  const seen: AllessehQueryRuleTerms[] = [];
  return rules.filter((rule) => {
    const normalizedRule = normalizeRule(rule);
    if (seen.some((seenRule) => isEqual(seenRule, normalizedRule))) {
      return false;
    }
    seen.push(normalizedRule);
    return true;
  });
}

function removeConflictingRulesInMerge(
  andRules: AllessehQueryRuleTerms[],
  notRules: AllessehQueryRuleTerms[]
): { and: AllessehQueryRuleTerms[]; not: AllessehQueryRuleTerms[] } {
  // Create working copies to avoid mutation
  const updatedAndRules = new Map<number, AllessehQueryRuleTerms>();
  const updatedNotRules = new Map<number, AllessehQueryRuleTerms>();

  // Helper to get the most recent version of a rule
  const getRule = (
    rules: AllessehQueryRuleTerms[],
    index: number,
    updatedMap: Map<number, AllessehQueryRuleTerms>
  ): AllessehQueryRuleTerms => updatedMap.get(index) ?? rules[index];

  // Step 1: Remove exact duplicates between 'and' and 'not'
  const exactDuplicatesToRemoveFromAnd = new Set<number>();
  const exactDuplicatesToRemoveFromNot = new Set<number>();

  andRules.forEach((andRule, andIndex) => {
    notRules.forEach((notRule, notIndex) => {
      const normalizedAndRule = normalizeRule(andRule);
      const normalizedNotRule = normalizeRule(notRule);

      if (isEqual(normalizedAndRule, normalizedNotRule)) {
        exactDuplicatesToRemoveFromAnd.add(andIndex);
        exactDuplicatesToRemoveFromNot.add(notIndex);
      }
    });
  });

  // Step 2: Remove overlapping values between 'and' and 'not' for the same key
  andRules.forEach((_, andIndex) => {
    if (exactDuplicatesToRemoveFromAnd.has(andIndex)) return;

    const andRule = getRule(andRules, andIndex, updatedAndRules);

    notRules.forEach((__, notIndex) => {
      if (exactDuplicatesToRemoveFromNot.has(notIndex)) return;

      const notRule = getRule(notRules, notIndex, updatedNotRules);

      // Only process if they have the same key
      if (andRule.terms.key !== notRule.terms.key) return;

      // Find overlapping values
      const andValues = andRule.terms.value;
      const notValues = notRule.terms.value;
      const overlappingValues = andValues.filter((val) => notValues.includes(val));

      if (overlappingValues.length > 0) {
        // Remove overlapping values from BOTH and and not
        const newAndValues = andValues.filter((val) => !overlappingValues.includes(val));
        const newNotValues = notValues.filter((val) => !overlappingValues.includes(val));

        updatedAndRules.set(andIndex, {
          terms: {
            key: andRule.terms.key,
            value: newAndValues.sort()
          }
        });

        updatedNotRules.set(notIndex, {
          terms: {
            key: notRule.terms.key,
            value: newNotValues.sort()
          }
        });
      }
    });
  });

  // Filter out rules marked for removal and rules with empty values
  const filteredAnd = andRules
    .map((_, i) => ({ rule: getRule(andRules, i, updatedAndRules), index: i }))
    .filter(({ rule, index }) => {
      if (exactDuplicatesToRemoveFromAnd.has(index)) return false;
      return rule.terms.value.length > 0;
    })
    .map(({ rule }) => rule);

  const filteredNot = notRules
    .map((_, i) => ({ rule: getRule(notRules, i, updatedNotRules), index: i }))
    .filter(({ rule, index }) => {
      if (exactDuplicatesToRemoveFromNot.has(index)) return false;
      return rule.terms.value.length > 0;
    })
    .map(({ rule }) => rule);

  return { and: filteredAnd, not: filteredNot };
}

function mergeQueries(queries: string[]) {
  const mergedQuery: {
    query: {
      and: AllessehQueryRuleTerms[];
      not: AllessehQueryRuleTerms[];
    };
  } = {
    query: {
      and: [],
      not: []
    }
  };

  // Track which keys appear in 'and' for each query
  const keysInAndPerQuery: Set<string>[] = [];
  const validQueriesCount = queries.filter((q) => q).length;

  queries.forEach((queryString) => {
    if (!queryString) return;
    const { query } = JSON.parse(queryString) as { query: { and?: []; not?: [] } };

    // Track keys in this query's 'and' clause
    const keysInThisQuery = new Set<string>();
    if (query.and) {
      query.and.forEach((clause: AllessehQueryRuleTerm | AllessehQueryRuleTerms) => {
        let key;
        let value;

        if ('term' in clause) {
          ({ key, value } = clause.term);
        } else {
          ({ key, value } = clause.terms);
        }

        keysInThisQuery.add(key);
        addToMergedQuery(mergedQuery.query.and, key, value);
      });
    }
    keysInAndPerQuery.push(keysInThisQuery);

    if (query.not) {
      query.not.forEach((clause: AllessehQueryRuleTerm | AllessehQueryRuleTerms) => {
        let key;
        let value;

        if ('term' in clause) {
          ({ key, value } = clause.term);
        } else {
          ({ key, value } = clause.terms);
        }

        addToMergedQuery(mergedQuery.query.not, key, value);
      });
    }
  });

  // Deduplicate rules within each clause
  mergedQuery.query.and = dedupeRules(mergedQuery.query.and);
  mergedQuery.query.not = dedupeRules(mergedQuery.query.not);

  // Remove conflicting rules between 'and' and 'not'
  const cleaned = removeConflictingRulesInMerge(mergedQuery.query.and, mergedQuery.query.not);
  mergedQuery.query.and = cleaned.and;
  mergedQuery.query.not = cleaned.not;

  // Remove keys from 'and' that don't appear in ALL queries
  // Count how many queries have each key
  const keyCount = new Map<string, number>();
  keysInAndPerQuery.forEach((keysSet) => {
    keysSet.forEach((key) => {
      keyCount.set(key, (keyCount.get(key) ?? 0) + 1);
    });
  });

  // Filter out keys that don't appear in all queries
  mergedQuery.query.and = mergedQuery.query.and.filter((rule) => {
    const count = keyCount.get(rule.terms.key) ?? 0;
    return count === validQueriesCount;
  });

  return mergedQuery;
}

enum BaseQueryType {
  BASE_ALLESSEH_QUERY = 'baseAllessehQuery',
  BASE_ALLESSEH_PRODUCT_CARD_QUERY = 'baseProductCardAllessehQuery',
  BASE_ALLESSEH_LIVE_COVERAGE_QUERY = 'baseLiveCoverageAllessehQuery',
  BASE_TAPPABLE_ALLESSEH_QUERY = 'baseTappableAllessehQuery',
  BASE_AUDIO_ALLESSEH_QUERY = 'baseAudioAllessehQuery',
  BASE_VIDEO_ALLESSEH_QUERY = 'baseVideoAllessehQuery',
  BASE_PODCAST_ALLESSEH_QUERY = 'basePodcastAllessehQuery',
  BASE_RECIPE_ALLESSEH_QUERY = 'baseRecipeAllessehQuery',
  BASE_WEBSTORY_ALLESSEH_QUERY = 'baseWebStoryAllessehQuery',
  BASE_OPINION_ALLESSEH_QUERY = 'baseOpinionAllessehQuery',
  BASE_MARKETWATCH_ALLESSEH_QUERY = 'baseMarketwatchAllessehQuery',
  BASE_MARKETWATCH_VIDEO_ALLESSEH_QUERY = 'baseMarketwatchVideoAllessehQuery',
  BASE_MARKETWATCH_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY = 'baseMarketwatchLiveCoverageEventsAllessehQuery',
  BASE_MARKETWATCH_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY = 'baseMarketwatchLiveCoverageCardsAllessehQuery',
  BASE_PENTA_ALLESSEH_QUERY = 'basePentaAllessehQuery',
  BASE_AFP_ALLESSEH_QUERY = 'baseAfpAllessehQuery',
  BASE_WSJ_ALLESSEH_QUERY = 'baseWsjAllessehQuery',
  BASE_WSJ_VIDEO_ALLESSEH_QUERY = 'baseWsjVideoAllessehQuery',
  BASE_WSJ_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY = 'baseWsjLiveCoverageEventsAllessehQuery',
  BASE_WSJ_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY = 'baseWsjLiveCoverageCardsAllessehQuery',
  BASE_MANSION_GLOBAL_ALLESSEH_QUERY = 'baseMansionGlobalAllessehQuery',
  BASE_BARRONS_ALLESSEH_QUERY = 'baseBarronsAllessehQuery',
  BASE_BARRONS_VIDEO_ALLESSEH_QUERY = 'baseBarronsVideoAllessehQuery',
  BASE_BARRONS_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY = 'baseBarronsLiveCoverageEventsAllessehQuery',
  BASE_BARRONS_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY = 'baseBarronsLiveCoverageCardsAllessehQuery',
  BASE_DOW_JONES_RISK_JOURNAL_DJ_EVENTS_ALLESSEH_QUERY = 'baseDowJonesRiskJournalDJEventsAllessehQuery',
  BASE_DOW_JONES_RISK_JOURNAL_CCO_EVENTS_ALLESSEH_QUERY = 'baseDowJonesRiskJournalCCOEventsAllessehQuery',
  BASE_DOW_JONES_RISK_JOURNAL_CONTRIBUTOR_ALLESSEH_QUERY = 'baseDowJonesRiskJournalContributorAllessehQuery',
  BASE_WORLD_ECR_ALLESSEH_QUERY = 'baseWorldECRAllessehQuery',
  BASE_EXPORT_COMPLIANCE_MANAGER_ALLESSEH_QUERY = 'baseExportComplianceManagerAllessehQuery',
  BASE_OXFORD_ANALYTICA_ALLESSEH_QUERY = 'baseOxfordAnalyticaAllessehQuery',
  BASE_OXFORD_ANALYTICA_ANNOUNCEMENTS_ALLESSEH_QUERY = 'baseOxfordAnalyticaAnnouncementsAllessehQuery',
  BASE_DRAGONFLY_ALLESSEH_QUERY = 'baseDragonflyAllessehQuery',
  BASE_WSJ_OPINION_VIDEO_ALLESSEH_QUERY = 'baseWSJOpinionVideoAllessehQuery',
  BASE_PENEWS_ALLESSEH_QUERY = 'basePENEWSAllessehQuery',
  BASE_FNONLINE_ALLESSEH_QUERY = 'baseFNOnlineAllessehQuery',
  BASE_WSJ_PRO_ALLESSEH_QUERY = 'baseWsjProAllessehQuery',
  BASE_WSJ_PRO_PE_ALLESSEH_QUERY = 'baseWsjProPEAllessehQuery',
  BASE_BARRONS_INVESTOR_CIRCLE_ALLESSEH_QUERY = 'baseBarronsInvestorCircleAllessehQuery',
  BASE_FREE_EXPRESSION_ALLESSEH_QUERY = 'baseFreeExpressionAllessehQuery',
  BASE_IBD_ALLESSEH_QUERY = 'baseIbdAllessehQuery',
  BASE_BARRONS_BLOG_ALLESSEH_QUERY = 'baseBarronsBlogAllessehQuery',
  BASE_OPIS_ALLESSEH_QUERY = 'baseOpisAllessehQuery',
  BASE_ENERGY_NEWS_EVENTS_ALLESSEH_QUERY = 'baseEnergyNewsEventsAllessehQuery',
  BASE_PRIVATE_MARKET_EVENTS_ALLESSEH_QUERY = 'basePrivateMarketEventsAllessehQuery'
}

const CONTENT_TYPE_MAP = {
  Article: BaseQueryType.BASE_ALLESSEH_QUERY,
  CommerceProduct: BaseQueryType.BASE_ALLESSEH_PRODUCT_CARD_QUERY,
  Audio: BaseQueryType.BASE_AUDIO_ALLESSEH_QUERY,
  Video: BaseQueryType.BASE_VIDEO_ALLESSEH_QUERY,
  LiveCoverage: BaseQueryType.BASE_ALLESSEH_LIVE_COVERAGE_QUERY,
  LiveCoverageCard: BaseQueryType.BASE_ALLESSEH_LIVE_COVERAGE_QUERY,
  LiveCoverageStack: BaseQueryType.BASE_ALLESSEH_LIVE_COVERAGE_QUERY,
  Tappable: BaseQueryType.BASE_TAPPABLE_ALLESSEH_QUERY,
  Podcast: BaseQueryType.BASE_PODCAST_ALLESSEH_QUERY,
  Recipe: BaseQueryType.BASE_RECIPE_ALLESSEH_QUERY,
  Webstory: BaseQueryType.BASE_WEBSTORY_ALLESSEH_QUERY,
  Opinion: BaseQueryType.BASE_OPINION_ALLESSEH_QUERY,
  Marketwatch: BaseQueryType.BASE_MARKETWATCH_ALLESSEH_QUERY,
  MarketwatchVideo: BaseQueryType.BASE_MARKETWATCH_VIDEO_ALLESSEH_QUERY,
  MarketwatchLiveCoverageEvents: BaseQueryType.BASE_MARKETWATCH_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY,
  MarketwatchLiveCoverageCards: BaseQueryType.BASE_MARKETWATCH_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY,
  Penta: BaseQueryType.BASE_PENTA_ALLESSEH_QUERY,
  MansionGlobal: BaseQueryType.BASE_MANSION_GLOBAL_ALLESSEH_QUERY,
  Afp: BaseQueryType.BASE_AFP_ALLESSEH_QUERY,
  Wsj: BaseQueryType.BASE_WSJ_ALLESSEH_QUERY,
  WsjVideo: BaseQueryType.BASE_WSJ_VIDEO_ALLESSEH_QUERY,
  WsjLiveCoverageEvents: BaseQueryType.BASE_WSJ_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY,
  WsjLiveCoverageCards: BaseQueryType.BASE_WSJ_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY,
  Barrons: BaseQueryType.BASE_BARRONS_ALLESSEH_QUERY,
  BarronsVideo: BaseQueryType.BASE_BARRONS_VIDEO_ALLESSEH_QUERY,
  BarronsLiveCoverageEvents: BaseQueryType.BASE_BARRONS_LIVE_COVERAGE_EVENTS_ALLESSEH_QUERY,
  BarronsLiveCoverageCards: BaseQueryType.BASE_BARRONS_LIVE_COVERAGE_CARDS_ALLESSEH_QUERY,
  RiskNews: BaseQueryType.BASE_ALLESSEH_QUERY,
  DowJonesRiskJournal: BaseQueryType.BASE_ALLESSEH_QUERY,
  DowJonesRiskJournalDJEvents: BaseQueryType.BASE_DOW_JONES_RISK_JOURNAL_DJ_EVENTS_ALLESSEH_QUERY,
  DowJonesRiskJournalCCOEvents: BaseQueryType.BASE_DOW_JONES_RISK_JOURNAL_CCO_EVENTS_ALLESSEH_QUERY,
  DowJonesRiskJournalContributor: BaseQueryType.BASE_DOW_JONES_RISK_JOURNAL_CONTRIBUTOR_ALLESSEH_QUERY,
  WorldECR: BaseQueryType.BASE_WORLD_ECR_ALLESSEH_QUERY,
  ExportComplianceManager: BaseQueryType.BASE_EXPORT_COMPLIANCE_MANAGER_ALLESSEH_QUERY,
  OxfordAnalytica: BaseQueryType.BASE_OXFORD_ANALYTICA_ALLESSEH_QUERY,
  OxanAnnouncements: BaseQueryType.BASE_OXFORD_ANALYTICA_ANNOUNCEMENTS_ALLESSEH_QUERY,
  Dragonfly: BaseQueryType.BASE_DRAGONFLY_ALLESSEH_QUERY,
  WSJOpinionVideo: BaseQueryType.BASE_WSJ_OPINION_VIDEO_ALLESSEH_QUERY,
  PENEWS: BaseQueryType.BASE_PENEWS_ALLESSEH_QUERY,
  FNOnline: BaseQueryType.BASE_FNONLINE_ALLESSEH_QUERY,
  WsjPro: BaseQueryType.BASE_WSJ_PRO_ALLESSEH_QUERY,
  WsjProPe: BaseQueryType.BASE_WSJ_PRO_PE_ALLESSEH_QUERY,
  WsjProPE: BaseQueryType.BASE_WSJ_PRO_PE_ALLESSEH_QUERY, // Alias for backend compatibility
  BarronsInvestorCircle: BaseQueryType.BASE_BARRONS_INVESTOR_CIRCLE_ALLESSEH_QUERY,
  BarronsBlog: BaseQueryType.BASE_BARRONS_BLOG_ALLESSEH_QUERY,
  FreeExpression: BaseQueryType.BASE_FREE_EXPRESSION_ALLESSEH_QUERY,
  IBD: BaseQueryType.BASE_IBD_ALLESSEH_QUERY,
  PrivateMarketEvents: BaseQueryType.BASE_PRIVATE_MARKET_EVENTS_ALLESSEH_QUERY,
  OPIS: BaseQueryType.BASE_OPIS_ALLESSEH_QUERY,
  EnergyNewsEvents: BaseQueryType.BASE_ENERGY_NEWS_EVENTS_ALLESSEH_QUERY
};

export enum BaseQueryOrigin {
  SEARCH = 'search',
  QUERY_BUILDER = 'query-builder'
}

interface getBaseQueryProps {
  contentTypes?: PublicationSettingSearchableContentType[];
  publicationSettings?: PublicationSetting;
  origin: BaseQueryOrigin;
  baseQueryConfigs?: QueryConfig[];
}

const NO_RESULTS_UPSTREAM_ORIGIN_ID = '__UCS_NO_RESULTS__';

/**
 * Base Allesseh query that is guaranteed to return no results.
 * Uses an impossible `UpstreamOriginId` value that cannot be present in indexed content.
 */
const NO_RESULTS_BASE_QUERY = JSON.stringify({
  query: {
    and: [{ terms: { key: 'UpstreamOriginId', value: [NO_RESULTS_UPSTREAM_ORIGIN_ID] } }],
    or: [],
    not: []
  }
});

const getQueryConfigForContentType = (contentType: string, baseQueryConfigs: QueryConfig[]): string | null => {
  const match = baseQueryConfigs.find((qc) => qc.enabled && qc.name.toLowerCase() === contentType.toLowerCase());
  return match ? JSON.stringify(match.queryBody) : null;
};

export const getBaseQuery = ({ contentTypes, publicationSettings, origin, baseQueryConfigs }: getBaseQueryProps) => {
  if (publicationSettings && contentTypes) {
    if (origin === BaseQueryOrigin.SEARCH && contentTypes.length === 0) {
      return NO_RESULTS_BASE_QUERY;
    }
    // For SEARCH, an explicitly empty contentTypes array is handled above by returning a no-results query.
    // For QUERY_BUILDER, always use the base Allesseh query; otherwise, fall back to queries for the specified contentTypes.
    const enabledQueryConfigs = baseQueryConfigs?.filter((qc) => qc.enabled) ?? [];
    // fallback to baseQuery if content types are not specified.
    const queries =
      origin === BaseQueryOrigin.QUERY_BUILDER
        ? [publicationSettings[BaseQueryType.BASE_ALLESSEH_QUERY]]
        : contentTypes.reduce((queries: string[], contentType: PublicationSettingSearchableContentType) => {
            // Prefer a matching enabled QueryConfig by name; fall back to the hardcoded base query field.
            const queryConfigMatch = getQueryConfigForContentType(contentType, enabledQueryConfigs);
            if (queryConfigMatch) {
              queries.push(queryConfigMatch);
            } else if (contentType in CONTENT_TYPE_MAP) {
              const key = contentType as keyof typeof CONTENT_TYPE_MAP;
              const queryKey = CONTENT_TYPE_MAP[key];
              const query = publicationSettings[queryKey] as string;
              queries.push(query);
            }
            return queries;
          }, []);

    const mergedBaseAllessehQuery = mergeQueries(queries);
    return JSON.stringify(mergedBaseAllessehQuery);
  }

  return '{}';
};

/**
 * Util function to check if the content type of an Allesseh item is an article or media type.
 * @param contentType The content type of the Allesseh item
 */
export const isArticleOrMediaContentType = (contentType: string) => {
  const articleOrMediaContentTypes = [
    'article',
    'card',
    'bulletin',
    'pulse',
    'audio',
    'video',
    'podcast',
    'sponsored',
    'webstory',
    'commerceproduct',
    'recipe'
  ];

  return articleOrMediaContentTypes.includes(contentType.toLowerCase());
};

export interface CurationContentType {
  contentTreatment: string;
  contentType: string;
  storyType: string;
}

export const getCurationContentType = (
  item: ArticleItem | MobileArticleItem | MobileLinkedItem
): CurationContentType | undefined => {
  if ('subtype' in item && item.subtype === MOBILE_LINKED_ITEM) {
    return {
      contentType: 'linked-item',
      storyType: 'news',
      contentTreatment: 'standard'
    };
  }
  const parsedContent: {
    curationContentType: CurationContentType;
  } = safelyParseContent((item as ArticleItem | MobileArticleItem).content);

  return parsedContent.curationContentType;
};
